import { useRef, useState } from "react";
import { UploadCloud, X, Images } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MediaLibrary from "./MediaLibrary";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = "anonymous";
    img.src = src;
  });

// Vorab-Crop auf Zielformat (z. B. 256x256, "top"-crop für Fahrerfotos)
async function cropToBlob(file, { targetW, targetH, cropFrom }) {
  const src = await fileToDataUrl(file);
  const img = await loadImage(src);
  const targetRatio = targetW / targetH;
  const srcRatio = img.width / img.height;
  let sw, sh, sx, sy;
  if (srcRatio > targetRatio) {
    sh = img.height; sw = sh * targetRatio;
    sx = (img.width - sw) / 2; sy = 0;
  } else {
    sw = img.width; sh = sw / targetRatio;
    sx = 0; sy = cropFrom === "top" ? 0 : (img.height - sh) / 2;
  }
  const c = document.createElement("canvas");
  c.width = targetW; c.height = targetH;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, targetW, targetH);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
  return await new Promise((resolve) => c.toBlob((b) => resolve(b), "image/png"));
}

// Lädt eine Datei via signed-URL nach Cloudinary hoch und gibt {secure_url, public_id} zurück
async function uploadToCloudinary(blob, folder, publicId) {
  const query = new URLSearchParams({ folder });
  if (publicId) query.set("public_id", publicId);
  const { data: sig } = await api.get(`/cloudinary/signature?${query.toString()}`);
  const form = new FormData();
  form.append("file", blob);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);
  if (sig.public_id) form.append("public_id", sig.public_id);
  if (sig.overwrite) form.append("overwrite", "true");
  if (sig.invalidate) form.append("invalidate", "true");
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
    method: "POST", body: form,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Cloudinary HTTP ${res.status}: ${txt.slice(0, 200)}`);
  }
  const data = await res.json();
  return { secure_url: data.secure_url, public_id: data.public_id };
}

// Wandelt eine Cloudinary-URL in eine optimierte Variante um (auto format + quality, gleiche Größe)
export function cldOptimize(url) {
  if (!url || !url.includes("/upload/")) return url;
  return url.replace("/upload/", "/upload/f_auto,q_auto/");
}

export default function ImageUploader({
  value,
  onChange,
  testIdPrefix = "img",
  targetW = 256,
  targetH = 256,
  cropFrom = "center",
  hint = "PNG / JPG · wird auf 256×256 zugeschnitten · upload zu Cloudinary",
  previewSize = "w-16 h-16",
  folder = "drivers",
  publicId,
}) {
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen.");
      return;
    }
    setLoading(true);
    try {
      const blob = await cropToBlob(file, { targetW, targetH, cropFrom });
      const { secure_url } = await uploadToCloudinary(blob, folder, publicId);
      onChange(secure_url);
      toast.success("Bild hochgeladen");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Bild-Upload fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="mt-1">
      {value ? (
        <div className="flex items-center gap-3 flex-wrap" data-testid={`${testIdPrefix}-preview-wrap`}>
          <div className={`${previewSize} border border-white/10 bg-[#0B0F19] flex items-center justify-center overflow-hidden`}>
            <img src={cldOptimize(value)} alt="Upload" className="w-full h-full object-cover" data-testid={`${testIdPrefix}-preview`} />
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost text-xs" data-testid={`${testIdPrefix}-replace`}>
            <UploadCloud className="w-3.5 h-3.5 mr-2 inline" /> Ersetzen
          </button>
          <button type="button" onClick={() => setLibOpen(true)} className="btn-ghost text-xs" data-testid={`${testIdPrefix}-library`}>
            <Images className="w-3.5 h-3.5 mr-2 inline" /> Mediathek
          </button>
          <button type="button" onClick={() => onChange("")} className="btn-ghost text-xs text-red-400" data-testid={`${testIdPrefix}-clear`}>
            <X className="w-3.5 h-3.5 mr-2 inline" /> Entfernen
          </button>
        </div>
      ) : (
        <>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            data-testid={`${testIdPrefix}-dropzone`}
            className={`cursor-pointer border-2 border-dashed px-4 py-6 text-center transition-colors ${drag ? "border-[#00E5FF] bg-[#00E5FF]/5" : "border-white/15 hover:border-white/30"}`}
          >
            {loading ? (
              <div className="text-sm text-neutral-400">Lade hoch…</div>
            ) : (
              <>
                <UploadCloud className="w-6 h-6 mx-auto text-[#00E5FF]" />
                <div className="text-sm font-bold mt-2">Bild hierher ziehen oder klicken</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{hint}</div>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => setLibOpen(true)}
            className="mt-2 text-[10px] uppercase tracking-widest text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1.5"
            data-testid={`${testIdPrefix}-library`}
          >
            <Images className="w-3 h-3" /> aus Mediathek wählen
          </button>
        </>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testIdPrefix}-input`}
      />
      <MediaLibrary
        open={libOpen}
        onClose={() => setLibOpen(false)}
        onSelect={(url) => { onChange(url); toast.success("Bild aus Mediathek übernommen"); }}
        defaultFolder={folder}
      />
    </div>
  );
}
